import isBoolean from 'lodash/isBoolean';
import isDate from 'lodash/isDate';
import isEqual from 'lodash/isEqual';
import isFinite from 'lodash/isFinite';
import isNumber from 'lodash/isNumber';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import isUndefined from 'lodash/isUndefined';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import pickBy from 'lodash/pickBy';
import moment from 'moment';
import toNumber from 'lodash/toNumber';
import isNil from 'lodash/isNil';

import {
  DATASET_MIXPANEL,
  EVENTS,
  NUMERIC_MATH_TYPES,
  PRIMITIVE_MATH_TYPES,
  PropertyType,
  ResourceType,
  SESSION_EVENT_NAMES,
  SESSION_EVENTS,
  SPECIAL_EVENTS,
  XAU_MATH_TYPES,
} from 'common/report/constants';
import {getAlphaLabelFromIndex} from 'common/util';
import {isPersistedCustomProperty} from 'common/widgets/custom-property-modal/util';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {DateRangeType, SpecialTimeUnit, TimeUnit, TimeUnitLabels} from 'common/widgets/date-range-picker/models/types';
import {
  INSIGHTS_BOOLEAN_MAPPING,
  INSIGHTS_BOOLEAN_TYPES,
  INSIGHTS_COHORT_TYPE,
  INSIGHTS_DATE_RANGE_MAPPING,
  INSIGHTS_DATE_RANGE_TYPES,
  INSIGHTS_LIST_TYPES,
  INSIGHTS_NUMBER_TYPES,
  INSIGHTS_PROPERTY_FILTER_RESOURCE_TYPE_MAPPING,
  INSIGHTS_RESOURCE_TYPES,
  INSIGHTS_STRING_TYPES,
  insightsFilterAttrsFromPropertyFilter,
  insightsFilterToPropertyFilter,
} from 'common/widgets/property-filter-menu/models/insights-filter';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {ObjectOperator} from 'common/widgets/property-filter-menu/models/types';

// TODO: This should use TimeUnit enum from mp-date-range-picker component
const TIME_UNITS = {
  MINUTE: TimeUnit.Minute,
  HOUR: TimeUnit.Hour,
  DAY: TimeUnit.Day,
  WEEK: TimeUnit.Week,
  MONTH: TimeUnit.Month,
  QUARTER: TimeUnit.Quarter,
  YEAR: TimeUnit.Year,
};

const TIME_UNIT_ADJECTIVES = {
  [TIME_UNITS.MINUTE]: `every minute`,
  [TIME_UNITS.HOUR]: `hourly`,
  [TIME_UNITS.DAY]: `daily`,
  [TIME_UNITS.WEEK]: `weekly`,
  [TIME_UNITS.MONTH]: `monthly`,
  [TIME_UNITS.QUARTER]: `quarterly`,
  [TIME_UNITS.YEAR]: `yearly`,
};

const TIME_UNIT_QUERY_LIST = [
  TIME_UNITS.HOUR,
  TIME_UNITS.DAY,
  TIME_UNITS.WEEK,
  TIME_UNITS.MONTH,
  TIME_UNITS.QUARTER,
  TIME_UNITS.YEAR,
];

const TIME_UNIT_LIST = [TIME_UNITS.MINUTE, ...TIME_UNIT_QUERY_LIST];

const TIME_UNIT_GROUPING_LIST = [...TIME_UNIT_QUERY_LIST, SpecialTimeUnit.HourOfDay, SpecialTimeUnit.DayOfWeek];

// TODO: This should use DateRangeType enum from mp-date-range-picker component
const DATE_RANGE_TYPE = {
  BETWEEN: DateRangeType.Between,
  ON: DateRangeType.On,
  RELATIVE: DateRangeType.RelativeAfter,
  SINCE: DateRangeType.Since,
};

export class Clause {
  static create(sectionType, attrs, syncCustomEvent = null) {
    switch (sectionType) {
      case ShowClause.TYPE:
        return new ShowClause(attrs, syncCustomEvent);
      case GroupClause.TYPE:
        return new GroupClause(attrs);
      case FilterClause.TYPE:
        return new FilterClause(attrs);
      case FormulaClause.TYPE:
        return new FormulaClause(attrs);
      case TimeClause.TYPE:
        return new TimeClause(attrs);
      case CohortsClause.TYPE:
        return new CohortsClause(attrs);
    }
  }

  constructor({dataset} = {}) {
    // We've already migrated bookmarks in appdb to use "$mixpanel", however there can
    // still be shared links that use "mixpanel". This will ensure these links will be
    // fixed, and that when subsequent bookmarks created from these links they will be
    // saved with "$mixpanel" dataset.
    this.dataset = dataset === `mixpanel` ? DATASET_MIXPANEL : dataset;
  }

  get attrs() {
    return pick(this, [`dataset`]);
  }

  extend(attrs = {}) {
    return this.validate(Clause.create(this.TYPE, {...this.attrs, ...attrs}));
  }

  formattedType() {
    return {
      [CohortsClause.TYPE]: `Cohorts`,
      [ShowClause.TYPE]: `Compare`,
      [GroupClause.TYPE]: `Group by`,
      [FilterClause.TYPE]: `Filter`,
      [FormulaClause.TYPE]: `Formula`,
      [TimeClause.TYPE]: `Time`,
    }[this.TYPE];
  }

  serialize() {
    return this.attrs;
  }

  trackingData() {
    return this.serialize();
  }

  toBookmarkData() {
    return pickBy(this.attrs, (value) => !isUndefined(value));
  }

  toUrlData() {
    return this.attrs;
  }

  get valid() {
    return true;
  }

  validate(newClause) {
    return newClause;
  }
}
Clause.RESOURCE_TYPE_ALL = Clause.prototype.RESOURCE_TYPE_ALL = INSIGHTS_RESOURCE_TYPES.RESOURCE_TYPE_ALL;
Clause.RESOURCE_TYPE_EVENTS = Clause.prototype.RESOURCE_TYPE_EVENTS = INSIGHTS_RESOURCE_TYPES.RESOURCE_TYPE_EVENTS;
Clause.RESOURCE_TYPE_PEOPLE = Clause.prototype.RESOURCE_TYPE_PEOPLE = INSIGHTS_RESOURCE_TYPES.RESOURCE_TYPE_PEOPLE;
Clause.RESOURCE_TYPE_COHORTS = Clause.prototype.RESOURCE_TYPE_COHORTS = INSIGHTS_RESOURCE_TYPES.RESOURCE_TYPE_COHORTS;
Clause.RESOURCE_TYPE_DATAGROUP = Clause.prototype.RESOURCE_TYPE_DATAGROUP =
  INSIGHTS_RESOURCE_TYPES.RESOURCE_TYPE_DATAGROUP;
Clause.RESOURCE_TYPES = Clause.prototype.RESOURCE_TYPES = [
  Clause.RESOURCE_TYPE_ALL,
  Clause.RESOURCE_TYPE_EVENTS,
  Clause.RESOURCE_TYPE_PEOPLE,
  Clause.RESOURCE_TYPE_COHORTS,
  Clause.RESOURCE_TYPE_DATAGROUP,
];

export const QUERY_BUILDER_RESOURCE_TYPE_MAPPING = {
  [ResourceType.Cohort]: Clause.RESOURCE_TYPE_COHORTS,
  [ResourceType.Event]: Clause.RESOURCE_TYPE_EVENTS,
  [ResourceType.User]: Clause.RESOURCE_TYPE_PEOPLE,
};

function sanitizeIfCustomProperty(property) {
  if (!property.customPropertyId) {
    return property;
  }

  const {customProperty} = property;
  return {
    ...omit(property, [`customProperty`]),
    // customProperty (custom property definition) should not be passed as an attr for persisted
    // custom properties. However, old bookmark did store customProperty for persisted custom
    // properties, which we remove below.
    ...(!isPersistedCustomProperty(property.customPropertyId) && customProperty ? {customProperty} : {}),
  };
}

export class EventsPropertiesClause extends Clause {
  constructor(attrs = {}) {
    super(...arguments);
    this.value = attrs.value || null;
    this.resourceType = attrs.resourceType || Clause.RESOURCE_TYPES[0];
    this.profileType = attrs.profileType || null;
    this.search = attrs.search || ``;
    this.dataGroupId = attrs.dataGroupId || null;
    if (attrs.tempDataGroupId) {
      this.tempDataGroupId = attrs.tempDataGroupId;
    }

    Object.assign(this, sanitizeIfCustomProperty(pick(attrs, [`customPropertyId`, `customProperty`])));
  }

  get attrs() {
    return {
      ...super.attrs,
      ...pick(this, [
        `behavior`,
        `customProperty`,
        `customPropertyId`,
        `value`,
        `resourceType`,
        `profileType`,
        `search`,
        `dataGroupId`,
        `tempDataGroupId`,
      ]),
    };
  }

  get valid() {
    return (
      super.valid &&
      this.RESOURCE_TYPES.includes(this.resourceType) &&
      isString(this.search) &&
      (!this.isCustomProperty || this.isCustomPropertyValid())
    );
  }

  isCustomPropertyValid() {
    return (
      this.customPropertyId ||
      (this.customProperty.name && this.customProperty.composedProperties && this.customProperty.displayFormula)
    );
  }

  get isCustomProperty() {
    return !!this.customProperty || !!this.customPropertyId;
  }

  get source() {
    return this.profileType || this.resourceType;
  }

  getProjectId() {
    // TODO(davidhu): Remove this.dataset and directly pass in the relevant projectId
    if (typeof this.dataset === `string` && this.dataset) {
      const parsedProjectId = toNumber(this.dataset.split(`|`)[0]);
      if (isFinite(parsedProjectId)) {
        return parsedProjectId;
      }
    }
    return null;
  }

  toUrlData() {
    return {
      ...super.toUrlData(),
      ...pick(this.attrs, [
        `behavior`,
        `customProperty`,
        `customPropertyId`,
        `value`,
        `resourceType`,
        `profileType`,
        `dataGroupId`,
        `tempDataGroupId`,
      ]),
    };
  }
}

export class ShowClause extends EventsPropertiesClause {
  static computeFormulaLabel(clauseIndex) {
    return getAlphaLabelFromIndex(clauseIndex);
  }

  get COMMON_ATTRS() {
    return [
      `resourceType`,
      `math`,
      `perUserAggregation`,
      `property`,
      `filterDeterminer`,
      `value`,
      `customBucket`,
      `multiAttribution`,
      `rolling`,
      `cumulative`,
    ];
  }

  constructor(attrs = {}, syncCustomEvent = null) {
    super(...arguments);
    this.math = attrs.math || this.MATH_TYPE_TOTAL;
    this.perUserAggregation = attrs.perUserAggregation || null;
    this.rolling = attrs.rolling || null;
    this.cumulative = attrs.cumulative || false;
    // TODO: Move sanitization logic app api bookmark endpoint
    this.property = attrs.property ? omit(sanitizeIfCustomProperty(attrs.property), [`label`]) : null;
    this.formulaLabel = attrs.formulaLabel || null;

    if (this.resourceType === Clause.RESOURCE_TYPE_PEOPLE) {
      this.profileType = Clause.RESOURCE_TYPE_PEOPLE;
    }

    if (this.value && this.value.custom && syncCustomEvent) {
      // If this is a custom event, pull latest data from the custom events list
      // in case it has been modified since the last time the report was saved.
      this.value = syncCustomEvent(this.value);
    }

    if (this.value && this.value.session_event) {
      // If this is a session event formatted for querying, convert it to the UI format
      // (e.g. from {session_event: 'start'} to {name: '$session_start'})
      this.value = {
        dataset: DATASET_MIXPANEL,
        name: SESSION_EVENT_NAMES[this.value.session_event],
        ...(this.value.renamed && {renamed: this.value.renamed}),
      };
    }

    if (attrs.filter && attrs.filter.length) {
      this.filterDeterminer = attrs.filterDeterminer;
      this.filter = attrs.filter.map((clauseAttrs) => new FilterClause(clauseAttrs));
    }

    if (attrs.customBucket) {
      this.customBucket = attrs.customBucket;
    }

    if (attrs.multiAttribution) {
      this.multiAttribution = attrs.multiAttribution;
    }
  }

  serializeFilter() {
    if (this.filter && this.filter.length) {
      const filters = this.filter.map((clause) => clause.serialize());
      // Assign determiner to the first filter
      filters[0].determiner = this.filterDeterminer;

      return {filter: filters};
    } else {
      return {};
    }
  }

  getFilterLabel() {
    if (!this.filter) {
      return ``;
    }

    return PropertyFilter.getCombinedLabel(
      this.filter.map((clause) => clause.toPropertyFilter()),
      this.filterDeterminer === `any` ? `or` : `and`,
      this.filter.length,
    );
  }

  getInlineCohort() {
    return this.value && this.value.raw_cohort ? this.value.raw_cohort : null;
  }

  get isInlineCohort() {
    return !!this.getInlineCohort();
  }

  get attrs() {
    return {
      ...super.attrs,
      ...this.serializeFilter(),
      ...(this.formulaLabel ? {formulaLabel: this.formulaLabel} : {}),
      ...pick(this, this.COMMON_ATTRS),
    };
  }

  get customEventId() {
    return this.value.custom && !this.value.is_collect_everything_event ? this.value.id : null;
  }

  get valid() {
    const isValidValue = !this.value || !(isObject(this.value) && Object.keys(this.value).length === 0); // don't allow empty object as value
    const isValidMathType = [
      ...this.MATH_TYPES,
      ...this.NUMERICAL_ONLY_MATH_TYPES,
      ...this.XAU_ONLY_MATH_TYPES,
    ].includes(this.math);
    const isValidFormulaLabel = !this.formulaLabel || /^[A-Z]+$/.test(this.formulaLabel);
    return super.valid && isValidValue && isValidMathType && isValidFormulaLabel;
  }

  toBookmarkData() {
    return pickBy(this.toUrlData({expandSessions: true}), (value) => !isUndefined(value));
  }

  isXauClause() {
    return this.XAU_ONLY_MATH_TYPES.includes(this.math);
  }

  isRollingClause() {
    return !!this.rolling;
  }

  isCumulativeClause() {
    return !!this.cumulative;
  }

  _pickValueEntries({extraFields = [], expandSessions} = {}) {
    let value = this.value || {};
    let valueFields = [];
    if (value.resourceType === Clause.RESOURCE_TYPE_COHORTS) {
      valueFields = [`id`, `name`, `raw_cohort`];
    } else if (value.custom) {
      valueFields = [`id`, `custom`];
    } else {
      valueFields = [`name`];
    }

    // Support for sessions
    if (expandSessions && SESSION_EVENTS[value.name]) {
      value = {
        ...value,
        ...SESSION_EVENTS[value.name],
      };
      valueFields = [...valueFields, `session_event`];
    }
    return pick(value, [...valueFields, ...extraFields]);
  }

  toUrlData({expandSessions} = {}) {
    return {
      ...super.toUrlData(),
      ...(this.formulaLabel ? {formulaLabel: this.formulaLabel} : {}),
      ...pick(this, this.COMMON_ATTRS),
      ...this.serializeFilter(),
      value: this._pickValueEntries({extraFields: [`resourceType`, `renamed`], expandSessions}),
    };
  }
}

ShowClause.TYPE = ShowClause.prototype.TYPE = `show`;
ShowClause.TOP_EVENTS = ShowClause.prototype.TOP_EVENTS = {
  name: SPECIAL_EVENTS.TOP_EVENTS,
  label: EVENTS[SPECIAL_EVENTS.TOP_EVENTS],
  icon: `star-top-events`,
  resourceType: `events`,
  custom: false,
  special: true,
};
ShowClause.ALL_EVENTS = ShowClause.prototype.ALL_EVENTS = {
  name: SPECIAL_EVENTS.ALL_EVENTS,
  label: EVENTS[SPECIAL_EVENTS.ALL_EVENTS],
  icon: `star-top-events`,
  resourceType: Clause.RESOURCE_TYPE_EVENTS,
  custom: false,
  special: true,
};
ShowClause.ALL_PEOPLE = ShowClause.prototype.ALL_PEOPLE = {
  name: `$all_people`,
  label: `All People`,
  icon: `profile`,
  resourceType: Clause.RESOURCE_TYPE_PEOPLE,
  custom: false,
  special: true,
};

ShowClause.MATH_TYPE_TOTAL = ShowClause.prototype.MATH_TYPE_TOTAL = PRIMITIVE_MATH_TYPES.TOTAL;
ShowClause.MATH_TYPE_UNIQUE = ShowClause.prototype.MATH_TYPE_UNIQUE = PRIMITIVE_MATH_TYPES.UNIQUE;
ShowClause.MATH_TYPE_AVERAGE = ShowClause.prototype.MATH_TYPE_AVERAGE = PRIMITIVE_MATH_TYPES.AVERAGE;
ShowClause.MATH_TYPE_MEDIAN = ShowClause.prototype.MATH_TYPE_MEDIAN = PRIMITIVE_MATH_TYPES.MEDIAN;
ShowClause.MATH_TYPE_P90 = ShowClause.prototype.MATH_TYPE_P90 = NUMERIC_MATH_TYPES.P90;
ShowClause.MATH_TYPE_P99 = ShowClause.prototype.MATH_TYPE_P99 = NUMERIC_MATH_TYPES.P99;
ShowClause.MATH_TYPE_MIN = ShowClause.prototype.MATH_TYPE_MIN = PRIMITIVE_MATH_TYPES.MIN;
ShowClause.MATH_TYPE_MAX = ShowClause.prototype.MATH_TYPE_MAX = PRIMITIVE_MATH_TYPES.MAX;

ShowClause.MATH_TYPES = ShowClause.prototype.MATH_TYPES = Object.values(PRIMITIVE_MATH_TYPES);
ShowClause.NUMERICAL_ONLY_MATH_TYPES = ShowClause.prototype.NUMERICAL_ONLY_MATH_TYPES = Object.values(
  NUMERIC_MATH_TYPES,
);
ShowClause.XAU_ONLY_MATH_TYPES = ShowClause.prototype.XAU_ONLY_MATH_TYPES = Object.values(XAU_MATH_TYPES);

export class CohortsClause extends Clause {
  constructor(attrs = {}) {
    super(...arguments);
    this.id = attrs.id;
    this.name = attrs.name;
    this.negated = attrs.negated || false;
    // Raw cohorts are not saved on the backend
    // and instead are directly passed through
    // from query-api to arb

    // eslint-disable-next-line camelcase
    this.raw_cohort = attrs.raw_cohort || null;
    // eslint-disable-next-line camelcase
    this.data_group_id = attrs.data_group_id || ``;
  }

  get attrs() {
    const {id, name, negated} = this;
    return pickBy(
      {
        ...super.attrs,
        id,
        name,
        negated,
        raw_cohort: this.raw_cohort,
        ...(this.data_group_id ? {data_group_id: this.data_group_id} : {}),
      },
      (value) => !isUndefined(value),
    );
  }

  get valid() {
    return !!(super.valid && isBoolean(this.negated) && (this.raw_cohort || (this.id && this.name)));
  }

  get isInlineCohort() {
    return !!this.getInlineCohort();
  }

  getInlineCohort() {
    return this.raw_cohort && this.attrs;
  }

  toUrlData() {
    return this.attrs;
  }

  formattedName() {
    return this.negated ? `Not In ${this.name}` : this.name;
  }
}

CohortsClause.TYPE = CohortsClause.prototype.TYPE = Clause.RESOURCE_TYPE_COHORTS;

export const insightsGroupProperties = [
  `customProperty`,
  `customPropertyId`,
  `value`,
  `propertyType`,
  `resourceType`,
  `typeCast`,
  `unit`,
  `dataGroupId`,
  `tempDataGroupId`,
  `propertyObjectKey`,
  `behavior`,
];

export class GroupClause extends EventsPropertiesClause {
  constructor(attrs = {}) {
    super(...arguments);
    if (attrs.joinPropertyType) {
      this.joinPropertyType = attrs.joinPropertyType;
    }
    if (attrs.tempDataGroupId) {
      this.tempDataGroupId = attrs.tempDataGroupId;
    }
    this.propertyType = attrs.propertyType || null;
    this.typeCast = attrs.typeCast || this.getDefaultTypeCast(attrs);
    this.propertyObjectKey = attrs.propertyObjectKey;
    this.unit = attrs.unit || null;
    this.editing = attrs.editing || null;
    if (this.isDatetimeProperty && !this.editing && this.unit === undefined) {
      this.unit = TIME_UNITS.DAY;
    }
    if (attrs.customBucket) {
      this.customBucket = attrs.customBucket;
    }
    if (attrs.customGroups) {
      this.customGroups = attrs.customGroups;
    }
    if (attrs.behavior) {
      this.behavior = attrs.behavior;
    }

    //To determine whether it is a filter or not
    this.filterOperator = null;

    // Support for Single Segment in GroupClause (reassigns filterOperator here if necessary)
    for (const attrName of Object.keys(attrs)) {
      //Add all filter properties to the GroupClause if they exist
      if (!insightsGroupProperties.includes(attrName)) {
        this[attrName] = attrs[attrName];
      }
    }

    //Make sure unit and filterDateUnit are equal if filterDateUnit exists
    if (this.filterDateUnit) {
      this.unit = this.filterDateUnit;
    }

    if (attrs.listQuantifier) {
      this.listQuantifier = attrs.listQuantifier;
    }
    if (attrs.listItemGroup) {
      this.listItemGroup = attrs.listItemGroup;
    }

    // propertyFilter is strictly used for rendering purposes since it contains the unflattened version of filterValue and the generic (non Insights specific) DateRangeType filterOperator]
    // also used to extract `filterValue` for boolean properties for Insights.
    this.propertyFilter = insightsFilterToPropertyFilter(this);
  }

  get valid() {
    return super.valid && (this.isCustomProperty || this.isRegularPropertyValid());
  }

  isRegularPropertyValid() {
    const validIfDatetime = this.isDatetimeProperty ? !this.unit || TIME_UNIT_GROUPING_LIST.includes(this.unit) : true;

    return Object.values(this.PropertyType).includes(this.propertyType) && validIfDatetime;
  }

  get attrs() {
    const {
      joinPropertyType,
      propertyObjectKey,
      tempDataGroupId,
      propertyType,
      typeCast,
      unit,
      customBucket,
      customGroups,
      filterOperator,
      filterValue,
      filterType,
      listQuantifier,
      listItemGroup,
      filterJoinType,
    } = this;
    const isFilter = !isNil(this.filterOperator);
    const type = listItemGroup?.typeCast || listItemGroup?.propertyType || typeCast || propertyType;
    return {
      ...super.attrs,
      ...(joinPropertyType ? {joinPropertyType} : {}),
      ...(tempDataGroupId ? {tempDataGroupId} : {}),
      ...(propertyObjectKey ? {propertyObjectKey} : {}),
      propertyType,
      ...(isFilter ? {} : {typeCast}),
      ...(isFilter ? {defaultType: propertyType} : {}),
      ...(isFilter ? {filterDateUnit: unit} : {unit}),
      ...(customBucket && type === PropertyType.Number ? {customBucket} : {}),
      ...(customGroups && [PropertyType.String, PropertyType.List].includes(type) ? {customGroups} : {}),
      ...(filterOperator ? {filterOperator} : {}),
      ...(!isNil(filterValue) ? {filterValue} : {}),
      ...(filterType ? {filterType} : {}),
      ...(listQuantifier ? {listQuantifier} : {}),
      ...(listItemGroup ? {listItemGroup} : {}),
      ...(filterJoinType ? {filterJoinType} : {}),
    };
  }

  toUrlData() {
    const conditionalAttrs = {propertyType: this.propertyType};
    if (this.isDatetimeProperty) {
      conditionalAttrs.unit = this.unit || TIME_UNITS.DAY;
    }
    if (this.typeCast) {
      conditionalAttrs.typeCast = this.typeCast;
    }
    if (this.customBucket) {
      conditionalAttrs.customBucket = this.customBucket;
    }
    if (this.customGroups) {
      conditionalAttrs.customGroups = this.customGroups;
    }
    return {...super.toUrlData(), ...conditionalAttrs};
  }

  getDefaultTypeCast(attrs) {
    if (attrs.value === `campaign_id`) {
      return `string`;
    }

    return null;
  }

  get isDatetimeProperty() {
    const realType = this.typeCast || this.propertyType;
    return realType === `datetime`;
  }
}
GroupClause.TYPE = GroupClause.prototype.TYPE = `group`;
GroupClause.EVENT_DATE = GroupClause.prototype.EVENT_DATE = {
  label: `Date`,
  icon: `star-top-events`,
  name: `$date`,
  type: `datetime`,
  resourceType: `events`,
  special: true,
};
export const CREATE_CUSTOM_PROPERTY = {
  icon: `compose`,
  name: `$custom_property`,
  type: `custom`,
  special: true,
  hasPill: false,
};

GroupClause.CREATE_CUSTOM_EVENT_PROPERTY = GroupClause.prototype.CREATE_CUSTOM_EVENT_PROPERTY = Object.assign(
  {label: `Create event property...`, resourceType: `events`},
  CREATE_CUSTOM_PROPERTY,
);
GroupClause.CREATE_CUSTOM_PEOPLE_PROPERTY = GroupClause.prototype.CREATE_CUSTOM_PEOPLE_PROPERTY = Object.assign(
  {label: `Create people property...`, resourceType: `people`},
  CREATE_CUSTOM_PROPERTY,
);

GroupClause.PropertyType = GroupClause.prototype.PropertyType = Object.values(PropertyType);
GroupClause.PROPERTY_TYPECASTS = GroupClause.prototype.PROPERTY_TYPECASTS = [`string`, `number`, `boolean`];

// Time constants
export const START_OF_YESTERDAY = `$start_of_previous_day`;
export const END_OF_YESTERDAY = `$end_of_previous_day`;
export const START_OF_TODAY = `$start_of_current_day`;
export const END_OF_TODAY = `$end_of_current_day`;
const SPECIAL_RANGES_SET = new Set([START_OF_YESTERDAY, END_OF_YESTERDAY, START_OF_TODAY, END_OF_TODAY]);
const TODAY = `$today`;
export const RANGES = {
  TODAY: `Current day`,
  YESTERDAY: `Previous day`,
  HOURS: `Last 96 hours`,
  MONTH: `Last 30 days`,
  WEEKS: `Last 24 weeks`,
  YEAR: `Last 12 months`,
  CUSTOM: `Select a date range...`,
};
const RANGE_LIST = [
  RANGES.TODAY,
  RANGES.YESTERDAY,
  RANGES.HOURS,
  RANGES.MONTH,
  RANGES.WEEKS,
  RANGES.YEAR,
  RANGES.CUSTOM,
];
const RANGE_TO_VALUE_AND_UNIT = {
  [RANGES.TODAY]: {value: TODAY, unit: TIME_UNITS.HOUR},
  [RANGES.HOURS]: {value: 96, unit: TIME_UNITS.HOUR},
  [RANGES.MONTH]: {value: 30, unit: TIME_UNITS.DAY},
  [RANGES.WEEKS]: {value: 24, unit: TIME_UNITS.WEEK},
  [RANGES.YEAR]: {value: 12, unit: TIME_UNITS.MONTH},
};

export const RANGE_TO_TIME_CLAUSE_PARAMS = {
  [RANGES.TODAY]: {
    dateRangeType: DATE_RANGE_TYPE.SINCE,
    value: START_OF_TODAY,
    window: null,
    unit: TIME_UNITS.HOUR,
  },
  [RANGES.YESTERDAY]: {
    dateRangeType: DATE_RANGE_TYPE.BETWEEN,
    value: [START_OF_YESTERDAY, END_OF_YESTERDAY],
    window: null,
    unit: TIME_UNITS.HOUR,
  },
  [RANGES.HOURS]: {
    dateRangeType: DATE_RANGE_TYPE.RELATIVE,
    value: null,
    window: {
      value: 96,
      unit: TIME_UNITS.HOUR,
    },
    unit: TIME_UNITS.HOUR,
  },
  [RANGES.MONTH]: {
    dateRangeType: DATE_RANGE_TYPE.RELATIVE,
    value: null,
    window: {
      value: 30,
      unit: TIME_UNITS.DAY,
    },
    unit: TIME_UNITS.DAY,
  },
  [RANGES.WEEKS]: {
    dateRangeType: DATE_RANGE_TYPE.RELATIVE,
    value: null,
    window: {
      value: 24,
      unit: TIME_UNITS.WEEK,
    },
    unit: TIME_UNITS.WEEK,
  },
  [RANGES.YEAR]: {
    dateRangeType: DATE_RANGE_TYPE.RELATIVE,
    value: null,
    window: {
      value: 12,
      unit: TIME_UNITS.MONTH,
    },
    unit: TIME_UNITS.MONTH,
  },
};
const UNIT_AND_VALUE_TO_RANGE = {
  hour: {
    [TODAY]: RANGES.TODAY,
    96: RANGES.HOURS,
  },
  day: {
    30: RANGES.MONTH,
  },
  week: {
    24: RANGES.WEEKS,
  },
  month: {
    12: RANGES.YEAR,
  },
};

const NUMBER_OF_DAYS_IN_UNIT = {
  [TimeUnit.Minute]: 1 / 1440,
  [TimeUnit.Hour]: 1 / 24,
  [TimeUnit.Day]: 1,
  [TimeUnit.Week]: 7,
  [TimeUnit.Month]: 30,
  [TimeUnit.Quarter]: 90,
};

export function getNumberOfDaysInTimeClause(timeClause) {
  const [fromDate, toDate] = Array.isArray(timeClause.value) ? timeClause.value : [null, null];
  switch (timeClause.dateRangeType) {
    case DateRangeType.RelativeAfter:
      return Math.ceil(timeClause.window.value * NUMBER_OF_DAYS_IN_UNIT[timeClause.window.unit]);
    case DateRangeType.Since:
      return timeClause.value === START_OF_TODAY ? 1 : moment().diff(moment(fromDate), `days`);
    case DateRangeType.On:
      return 1;
    case DateRangeType.Between:
      // We could use moment#startOf for start of days but the ranges
      // need to be adjusted for project timezone
      if (fromDate === START_OF_TODAY && toDate === END_OF_TODAY) {
        return 1;
      }
      if (fromDate === START_OF_YESTERDAY && toDate === END_OF_YESTERDAY) {
        return 1;
      }
      return moment(toDate).diff(moment(fromDate), `days`);
    default:
      if (timeClause.value === TODAY) {
        return 1;
      } else if (isNumber(timeClause.value)) {
        return Math.ceil(timeClause.value * NUMBER_OF_DAYS_IN_UNIT[timeClause.unit]);
      } else {
        return moment(toDate).diff(moment(fromDate), `days`);
      }
  }
}

export class TimeClause extends Clause {
  static rangeToUnitValue(range) {
    return RANGE_TO_VALUE_AND_UNIT[range] || {};
  }

  static unitValueToRange(unit, value) {
    const unitVals = UNIT_AND_VALUE_TO_RANGE[unit];
    return (unitVals && unitVals[value]) || null;
  }

  constructor(attrs = {}) {
    super(...arguments);
    if (this.dataset) {
      // Parent add this.dataset, but it doesn't make sense for time clause.
      delete this.dataset;
    }

    let unit;
    let value;
    if (attrs.range && !this.dateRangeType) {
      // Legacy format, to be supported for backwards compatibility
      ({unit, value} = TimeClause.rangeToUnitValue(attrs.range));
    } else {
      this.dateRangeType = attrs.dateRangeType;
      if (attrs.window) {
        this.window = attrs.window;
      }
      ({unit, value} = attrs);
    }
    if (unit) {
      this.unit = unit;
    }
    if (value) {
      this.value = value;
    }
    if (attrs.rolling) {
      this.rolling = attrs.rolling;
    }
  }

  isValidNumberValue(value) {
    return isNumber(value) && value >= 0;
  }

  isValidValue(value) {
    return [
      this.isValidNumberValue(value),
      value === TODAY,
      isDate(value),
      Array.isArray(value) &&
        value.length <= 2 &&
        value.length > 0 &&
        (value.every(isString) || value.every(this.isValidNumberValue) || value.every(isDate)),
    ].some(Boolean);
  }

  isValidRelativeDate(date) {
    return isObject(date) && TimeClause.TIME_UNIT_LIST.includes(date.unit) && this.isValidNumberValue(date.value);
  }

  isValidCalendarDateArray(value) {
    return (
      this.isValidDateStringArray(value) ||
      (this.rolling && Array.isArray(value) && value.every((v) => this.isValidRelativeDate(v)))
    );
  }

  isValidDateStringArray(value) {
    return (
      Array.isArray(value) &&
      value.every(
        (v) => isString(v) && (SPECIAL_RANGES_SET.has(v) || moment(v, `YYYY-MM-DD`, /* strict */ true).isValid()),
      )
    );
  }

  // @override
  toUrlData() {
    let keys = [];
    if (this.dateRangeType) {
      // new datepicker
      keys = [`dateRangeType`, `unit`].concat(
        this.dateRangeType === TimeClause.DATE_RANGE_TYPE.RELATIVE ? [`window`] : [`value`, `rolling`],
      );
    } else {
      // TODO: Can this codepath be removed?
      keys = [`value`, `unit`];
    }

    return pick(this, keys);
  }

  serialize() {
    return this.toUrlData();
  }

  get attrs() {
    return {...super.attrs, ...pick(this, `dateRangeType`, `window`, `unit`, `value`, `rolling`)};
  }

  get valid() {
    const range = this.range;
    return [
      TimeClause.TIME_UNIT_LIST.includes(this.unit),
      this.dateRangeType !== TimeClause.DATE_RANGE_TYPE.RELATIVE ||
        (this.window && this.isValidRelativeDate(this.window)),
      this.dateRangeType !== TimeClause.DATE_RANGE_TYPE.SINCE ||
        range === RANGES.TODAY ||
        this.isValidDateStringArray(this.value),
      this.dateRangeType !== TimeClause.DATE_RANGE_TYPE.ON || this.isValidCalendarDateArray(this.value),
      this.dateRangeType !== TimeClause.DATE_RANGE_TYPE.BETWEEN ||
        this.isValidCalendarDateArray(this.value) ||
        range === RANGES.YESTERDAY,
      this.dateRangeType || this.isValidValue(this.value),
    ].every(Boolean);
  }

  // TODO opslead
  // eslint-disable-next-line sonarjs/cognitive-complexity
  getDateRange() {
    let options = {};
    let dateRangeType = this.dateRangeType;
    if (
      this.dateRangeType === TimeClause.DATE_RANGE_TYPE.RELATIVE ||
      (!this.dateRangeType && isNumber(this.value)) ||
      this.range === RANGES.TODAY ||
      this.range === RANGES.YESTERDAY
    ) {
      dateRangeType = TimeClause.DATE_RANGE_TYPE.RELATIVE;
      let optionsSource;
      if (this.window) {
        // new relative window, or range
        optionsSource = this.window;
      } else if (isNumber(this.value)) {
        // old window format
        optionsSource = this;
      } else {
        // current day, or yesterday
        optionsSource = {unit: this.unit || TimeClause.TIME_UNITS.HOUR};
      }

      options = pick(optionsSource, [`unit`, `value`]);
    }

    if (Array.isArray(this.value)) {
      const [from, to] = this.value;
      options = {from, to};
      if (!to) {
        dateRangeType = dateRangeType || TimeClause.DATE_RANGE_TYPE.SINCE;
      } else if (to === from) {
        dateRangeType = dateRangeType || TimeClause.DATE_RANGE_TYPE.ON;
      } else {
        dateRangeType = dateRangeType || TimeClause.DATE_RANGE_TYPE.BETWEEN;
      }
    }

    const {from, to, unit, value} = options;
    const dateRangeAttributes = {type: dateRangeType, from, to};
    if (isBoolean(this.rolling)) {
      dateRangeAttributes.rolling = this.rolling;
    }
    if (unit) {
      dateRangeAttributes.window = {unit, value};
    }

    return new DateRange(dateRangeAttributes);
  }

  /**
   * Returns the number of days queried, for tracking purposes.
   */
  getNumberOfDaysQueried() {
    return getNumberOfDaysInTimeClause(this.attrs);
  }

  get defaultWindow() {
    return {unit: TimeClause.TIME_UNITS.DAY};
  }

  get range() {
    if (!this.dateRangeType) {
      return TimeClause.unitValueToRange(this.unit, this.value);
    } else {
      const entry = Object.entries(RANGE_TO_TIME_CLAUSE_PARAMS).find(([, {dateRangeType, window, unit, value}]) => {
        switch (dateRangeType) {
          case TimeClause.DATE_RANGE_TYPE.RELATIVE:
            return isEqual(this.window, window) && this.unit === unit;
          case TimeClause.DATE_RANGE_TYPE.SINCE:
            return !this.window && this.value === value;
          case TimeClause.DATE_RANGE_TYPE.BETWEEN:
            return isEqual(this.value, value);
          default:
            return false;
        }
      });
      return entry ? entry[0] : null;
    }
  }
}

TimeClause.TYPE = TimeClause.prototype.TYPE = `time`;
TimeClause.DATE_RANGE_TYPE = TimeClause.prototype.DATE_RANGE_TYPE = DATE_RANGE_TYPE;
TimeClause.RANGES = TimeClause.prototype.RANGES = RANGES;
TimeClause.RANGE_LIST = TimeClause.prototype.RANGE_LIST = RANGE_LIST;
TimeClause.RANGE_TO_VALUE_AND_UNIT = TimeClause.prototype.RANGE_TO_VALUE_AND_UNIT = RANGE_TO_VALUE_AND_UNIT;
TimeClause.RANGE_TO_TIME_CLAUSE_PARAMS = TimeClause.prototype.RANGE_TO_TIME_CLAUSE_PARAMS = RANGE_TO_TIME_CLAUSE_PARAMS;
TimeClause.TIME_UNITS = TimeClause.prototype.TIME_UNITS = TIME_UNITS;
TimeClause.TIME_UNIT_ADJECTIVES = TimeClause.prototype.TIME_UNIT_ADJECTIVES = TIME_UNIT_ADJECTIVES;
TimeClause.TIME_UNIT_LIST = TimeClause.prototype.TIME_UNIT_LIST = TIME_UNIT_LIST;
TimeClause.TIME_UNIT_QUERY_LIST = TimeClause.prototype.TIME_UNIT_QUERY_LIST = TIME_UNIT_QUERY_LIST;
TimeClause.TIME_UNIT_GROUPING_LIST = TimeClause.prototype.TIME_UNIT_GROUPING_LIST = TIME_UNIT_GROUPING_LIST;
TimeClause.TIME_UNIT_LABELS = TimeClause.prototype.TIME_UNIT_LABELS = TimeUnitLabels;
TimeClause.TIME_UNIT_QUERY_SET = TimeClause.prototype.TIME_UNIT_QUERY_SET = new Set(TIME_UNIT_QUERY_LIST);
TimeClause.UNIT_AND_VALUE_TO_RANGE = TimeClause.prototype.UNIT_AND_VALUE_TO_RANGE = UNIT_AND_VALUE_TO_RANGE;
TimeClause.TODAY = TODAY;
TimeClause.START_OF_TODAY = START_OF_TODAY;
TimeClause.START_OF_YESTERDAY = START_OF_YESTERDAY;
TimeClause.END_OF_YESTERDAY = END_OF_YESTERDAY;

export class FilterClause extends EventsPropertiesClause {
  static defaultCohortsAttrs() {
    return {
      dataset: DATASET_MIXPANEL,
      filterType: `list`,
      filterOperator: `contains`,
      filterValue: [],
      resourceType: this.RESOURCE_TYPE_EVENTS,
      value: this.COHORTS,
    };
  }

  static defaultCohortsClause() {
    return new FilterClause(this.defaultCohortsAttrs());
  }

  constructor(attrs = {}) {
    super(...arguments);

    this.determiner = attrs.determiner;
    this.filterType = attrs.filterType;
    this.filterOperator = attrs.filterOperator;
    this.filterValue = attrs.filterValue === 0 || attrs.filterValue ? attrs.filterValue : null;
    this.propertyObjectKey = attrs.propertyObjectKey || null;

    if (attrs.behavior) {
      this.behavior = attrs.behavior;
    }

    if (attrs.filterJoinType) {
      this.filterJoinType = attrs.filterJoinType;
    }

    if (attrs.listQuantifier) {
      this.listQuantifier = attrs.listQuantifier;
    }

    if (attrs.listItemFilters) {
      this.listItemFilters = attrs.listItemFilters;
    }

    if (attrs.defaultType) {
      this.defaultType = attrs.defaultType;
    }

    if (attrs.filterDateUnit) {
      this.filterDateUnit = attrs.filterDateUnit;
    }

    if (this.value === this.COHORTS) {
      // ensure events resource type for a cohort filter
      this.resourceType = this.RESOURCE_TYPE_EVENTS;
    }

    this.propertyFilter = insightsFilterToPropertyFilter(this);
  }

  static attrsFromPropertyFilter(propertyFilter) {
    return insightsFilterAttrsFromPropertyFilter(propertyFilter);
  }

  static fromPropertyFilter(propertyFilter) {
    const attrs = this.attrsFromPropertyFilter({
      dataset: DATASET_MIXPANEL,
      ...propertyFilter,
    });
    return new FilterClause(attrs);
  }

  toPropertyFilter() {
    return this.propertyFilter;
  }

  get filterAttrs() {
    return pick(this, [
      `behavior`,
      `filterType`,
      `filterJoinType`,
      `listQuantifier`,
      `listItemFilters`,
      `defaultType`,
      `filterOperator`,
      `filterValue`,
      `filterDateUnit`,
      `propertyObjectKey`,
    ]);
  }

  get attrs() {
    return {
      ...super.attrs,
      ...this.filterAttrs,
    };
  }

  toUrlData() {
    return {
      ...super.toUrlData(),
      ...this.filterAttrs,
    };
  }

  get valid() {
    if (!this.filterType || !this.filterOperator) {
      return false;
    }

    // list item filters ignore their operators
    const isComplexListFilter = !!this.listQuantifier && this.filterType === PropertyType.Object;

    const isFilterTypeValid = this.FILTER_TYPES.includes(this.filterType);
    const isDefaultFilterTypeValid = !this.defaultType || this.FILTER_TYPES.includes(this.defaultType);
    const isFilterOperatorValid =
      isComplexListFilter || this.FILTER_OPERATORS[this.filterType].includes(this.filterOperator);

    return super.valid && isFilterTypeValid && isDefaultFilterTypeValid && isFilterOperatorValid;
  }

  get isCohortsFilter() {
    return this.value === FilterClause.COHORTS;
  }

  get isInlineCohort() {
    return !!this.getInlineCohort();
  }

  getInlineCohort() {
    const value = Array.isArray(this.filterValue) ? this.filterValue[0] : this.filterValue;

    if (!value || !value.cohort) {
      return null;
    }

    return value.cohort.raw_cohort ? value.cohort : null;
  }

  trackingData() {
    const trackingData = super.trackingData();
    if (this.isCohortsFilter) {
      trackingData.filterType = this.RESOURCE_TYPE_COHORTS;
      trackingData.filterOperator = trackingData.filterOperator === `contains` ? `users in` : `users not in`;
    }
    return trackingData;
  }
}
FilterClause.OBJECT_TYPES = ObjectOperator;
FilterClause.STRING_TYPES = INSIGHTS_STRING_TYPES;
FilterClause.NUMBER_TYPES = INSIGHTS_NUMBER_TYPES;
FilterClause.LIST_TYPES = INSIGHTS_LIST_TYPES;
FilterClause.DATE_RANGE_TYPES = INSIGHTS_DATE_RANGE_TYPES;
FilterClause.BOOLEAN_TYPES = INSIGHTS_BOOLEAN_TYPES;
FilterClause.DATE_RANGE_MAPPING = INSIGHTS_DATE_RANGE_MAPPING;
FilterClause.BOOLEAN_MAPPING = INSIGHTS_BOOLEAN_MAPPING;

FilterClause.COHORTS = FilterClause.prototype.COHORTS = INSIGHTS_COHORT_TYPE;
FilterClause.TYPE = FilterClause.prototype.TYPE = `filter`;
FilterClause.DEFAULT_FILTER_OPERATOR = FilterClause.prototype.FILTER_OPERATORS = {
  [PropertyType.Object]: `is set`,
  [PropertyType.String]: `equals`,
  [PropertyType.Number]: `is equal to`,
  [PropertyType.Datetime]: `was on`,
  [PropertyType.Boolean]: `is true`,
  [PropertyType.List]: `contains`,
};
FilterClause.FILTER_OPERATORS = FilterClause.prototype.FILTER_OPERATORS = {
  [PropertyType.String]: [
    FilterClause.STRING_TYPES.CONTAINS,
    FilterClause.STRING_TYPES.DOES_NOT_CONTAIN,
    FilterClause.STRING_TYPES.EQUALS,
    FilterClause.STRING_TYPES.DOES_NOT_EQUAL,
    FilterClause.STRING_TYPES.IS_SET,
    FilterClause.STRING_TYPES.IS_NOT_SET,
  ],
  [PropertyType.Number]: [
    FilterClause.NUMBER_TYPES.IS_BETWEEN,
    FilterClause.NUMBER_TYPES.IS_NOT_BETWEEN,
    FilterClause.NUMBER_TYPES.IS_AT_LEAST,
    FilterClause.NUMBER_TYPES.IS_AT_MOST,
    FilterClause.NUMBER_TYPES.IS_GREATER_THAN,
    FilterClause.NUMBER_TYPES.IS_LESS_THAN,
    FilterClause.NUMBER_TYPES.IS_EQUAL_TO,
    FilterClause.NUMBER_TYPES.IS_NOT_EQUAL_TO,
    FilterClause.NUMBER_TYPES.IS_SET,
    FilterClause.NUMBER_TYPES.IS_NOT_SET,
  ],
  [PropertyType.Datetime]: [
    FilterClause.DATE_RANGE_TYPES.WAS_ON,
    FilterClause.DATE_RANGE_TYPES.WAS_NOT_ON,
    FilterClause.DATE_RANGE_TYPES.WAS_BETWEEN,
    FilterClause.DATE_RANGE_TYPES.WAS_NOT_BETWEEN,
    FilterClause.DATE_RANGE_TYPES.WAS_LESS_THAN,
    FilterClause.DATE_RANGE_TYPES.WAS_IN_THE,
    FilterClause.DATE_RANGE_TYPES.WAS_NOT_IN_THE,
    FilterClause.DATE_RANGE_TYPES.WAS_BEFORE,
    FilterClause.DATE_RANGE_TYPES.WAS_SINCE,
    FilterClause.DATE_RANGE_TYPES.WAS_IN_THE_NEXT,
  ],
  [PropertyType.Boolean]: [FilterClause.BOOLEAN_TYPES.IS_TRUE, FilterClause.BOOLEAN_TYPES.IS_FALSE],
  [PropertyType.List]: [FilterClause.LIST_TYPES.CONTAINS, FilterClause.LIST_TYPES.DOES_NOT_CONTAIN],
  [PropertyType.Object]: [FilterClause.OBJECT_TYPES.Equals, FilterClause.OBJECT_TYPES.Set],
};

FilterClause.FILTER_TYPES = FilterClause.prototype.FILTER_TYPES = Object.values(PropertyType);

FilterClause.PROPERTY_FILTER_RESOURCE_TYPE_MAPPING = INSIGHTS_PROPERTY_FILTER_RESOURCE_TYPE_MAPPING;

const FORMULA_OPERATORS = [`+`, `-`, `*`, `/`, `(`, `)`];
export class FormulaClause extends Clause {
  constructor(attrs = {}) {
    super(...arguments);
    this.value = attrs.value;
    if (attrs.label) {
      this.label = attrs.label;
    }
    if (attrs.errorMessage) {
      this.errorMessage = attrs.errorMessage;
    }
  }

  get attrs() {
    return {
      ...super.attrs,
      ...pick(this, [`label`, `value`, `errorMessage`]),
    };
  }

  get valid() {
    return super.valid && !!this.value;
  }

  trackingData() {
    if (!this.valid) {
      return {};
    }
    const {label, value} = this;
    const operatorRegex = new RegExp(FORMULA_OPERATORS.map((op) => `\\${op}`).join(`|`), `g`);
    return {
      '# of clauses': value
        .replace(/\s/g, ``)
        .split(operatorRegex)
        .filter((operand) => operand !== ``).length,
      operators: value.match(operatorRegex) || [],
      named: !!label,
      ...super.trackingData(),
    };
  }
}
FormulaClause.TYPE = FormulaClause.prototype.TYPE = `formula`;
